// useRunningDuration — calcula duration_ms efetiva pra task em estado running.
//
// Por que existe (P1.15 Z2 / plano D-G4-G):
// - Backend canonical (P1.15 G4 F1) popula `tasks.duration_ms` APENAS em
//   estado terminal (completed/failed/cancelled). Em `status='running'`, fica
//   NULL — frontend mostrava vazio durante execução, bloqueando visão
//   operacional do admin.
// - Solução: hook computa `Date.now() - Date.parse(started_at)` em runtime
//   quando task está running E `duration_ms` ainda é null, e re-renderiza
//   a cada 1s via setInterval pra mostrar contador vivo.
// - Em estado terminal, retorna `duration_ms` direto (source-of-truth do
//   backend preservado, back-compat 100%).
//
// Uso:
//   const effectiveDurationMs = useRunningDuration(task);
//   const elapsedSec = durationMsToSeconds(effectiveDurationMs);
//
// Cleanup:
// - setInterval é cleared no unmount.
// - setInterval é cleared quando task sai de `running` (transição
//   running → done/failed/cancelled). Próximo render usa `duration_ms`
//   terminal direto.

import { useEffect, useState } from 'react';

/**
 * Shape mínimo aceito pelo hook. Compatível com `TaskUI` (board/card) e
 * `BackendTask` (detail sheet), ambos têm esses 3 campos.
 */
export interface UseRunningDurationInput {
  status: string;
  started_at?: string | null;
  duration_ms?: number | null;
}

/** Tick interval pra re-render do contador vivo (1s). */
const TICK_MS = 1000;

/**
 * Retorna `duration_ms` efetivo:
 * - status='running' + duration_ms==null + started_at presente
 *   → `Date.now() - Date.parse(started_at)` (re-renderizado a cada 1s)
 * - caso contrário → `duration_ms` cru (backend source-of-truth)
 */
export function useRunningDuration(task: UseRunningDurationInput): number | null {
  const isRunningWithoutDuration =
    task.status === 'running' &&
    (task.duration_ms === null || task.duration_ms === undefined) &&
    !!task.started_at;

  // `now` só é usado quando isRunningWithoutDuration; senão fica estagnado.
  const [now, setNow] = useState<number>(() => Date.now());

  useEffect(() => {
    if (!isRunningWithoutDuration) return;

    // Re-render a cada TICK_MS pra contador vivo. `now` é inicializado no
    // useState com Date.now(), então o primeiro render já mostra valor
    // correto sem precisar de tick síncrono dentro do effect (que dispararia
    // cascading render — react-hooks/set-state-in-effect).
    const id = setInterval(() => {
      setNow(Date.now());
    }, TICK_MS);

    return () => clearInterval(id);
  }, [isRunningWithoutDuration]);

  if (isRunningWithoutDuration) {
    const start = Date.parse(task.started_at as string);
    if (Number.isNaN(start)) return task.duration_ms ?? null;
    return Math.max(0, now - start);
  }

  return task.duration_ms ?? null;
}
